import { useCallback, useEffect, useState } from "react";

import { analytics } from "MVVM/Analytics";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useIsMobileApp } from "MVVM/Hooks/useIsMobileApp";
import { useUser } from "MVVM/Hooks/useUser";
import { abTestValue } from "MVVM/Utilities/AbTest";
import { readNext } from "MVVM/Utilities/Auth/loginRedirect";
import { ONBOARDING_ROLLOUT } from "./config";

// Permanent: changing it reshuffles every user mid-experiment.
export const ONBOARDING_SALT = "onboarding-v1";

const STORAGE_KEY = "onboardingVariant";
// Where nothing was stored (storage blocked), later screens follow the rollout.
const fallbackVariant = (): TOnboardingVariant =>
  ONBOARDING_ROLLOUT === "full" ? "new" : "control";
const OPT_IN_KEY = "onboardingOptIn";

// Lets a fresh test account see the new flow while the rollout is internal.
export const captureOnboardingOptIn = (search: string) => {
  const value = new URLSearchParams(search).get("onboarding");
  try {
    if (value === "new") localStorage.setItem(OPT_IN_KEY, "1");
    if (value === "off") localStorage.removeItem(OPT_IN_KEY);
  } catch {
    /* Storage blocked: the tester gets the current flow. */
  }
};

const hasOptIn = () => {
  try {
    return localStorage.getItem(OPT_IN_KEY) === "1";
  } catch {
    return false;
  }
};

export type TOnboardingVariant = "new" | "control" | "excluded";
export type TOnboardingStep =
  | "welcome"
  | "connect"
  | "share_codes"
  | "steam_bot"
  | "autocapture"
  | "cs2_hud"
  | "first_clip";
type TStepAction = "Viewed" | "Completed" | "Skipped";

// Invites, referrals, team links and embed claims keep their own flows.
export const isExcludedSignup = (next: string | null, isMobileApp: boolean) =>
  isMobileApp ||
  !!next?.match(/^\/(invite|register)\?.*\bref=|\/team\/|^\/clip\?clip=/);

export const bucketToVariant = (value: number): TOnboardingVariant =>
  value < 0.5 ? "new" : "control";

const readStored = (userId?: string): TOnboardingVariant | null => {
  if (!userId) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return stored?.userId === userId ? stored.variant : null;
  } catch {
    return null;
  }
};

export const getSignupMethod = (user?: object): string => {
  const accounts = user as
    | Record<string, { usedForSignUp?: boolean } | undefined>
    | undefined;
  const methods = ["steam", "faceit", "riot", "epic", "battlenet", "tiktok"];
  return methods.find((m) => accounts?.[m]?.usedForSignUp) ?? "other";
};

// Assigned once on /welcome and stored per user so later screens agree.
export const useOnboardingVariant = ({ assign = false } = {}) => {
  const { allstarUser } = useUser();
  const { isMobileApp } = useIsMobileApp();
  const isInternal = useIsInternalAccount();
  const userId = allstarUser?.user?._id;
  const [stored, setStored] = useState(() => readStored(userId));

  useEffect(() => {
    const existing = readStored(userId);
    if (existing || !assign || !userId || ONBOARDING_ROLLOUT === "off")
      return setStored(existing);

    const save = (
      variant: TOnboardingVariant,
      properties: Record<string, unknown> = {},
    ) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, variant }));
      } catch {
        /* Falls back to control on later screens. */
      }
      setStored(variant);
      analytics.identify(userId, { onboardingVariant: variant });
      analytics.track("Onboarding - Variant Assigned", {
        variant,
        ...properties,
      });
    };

    if (isExcludedSignup(readNext(), isMobileApp)) return save("excluded");
    if (ONBOARDING_ROLLOUT === "internal") {
      // Not stored or tracked, so the real test starts with a clean slate.
      if (!isInternal && !hasOptIn()) return setStored("control");
      return save("new", { internalTest: true });
    }
    if (ONBOARDING_ROLLOUT === "full") return save("new");
    if (!window.crypto?.subtle) return save("control");
    // A failed hash must not leave /welcome on its spinner.
    abTestValue(userId, ONBOARDING_SALT)
      .then((value) => save(bucketToVariant(value), { bucketValue: value }))
      .catch(() => save("control"));
  }, [userId, assign, isMobileApp, isInternal]);

  const variant: TOnboardingVariant =
    ONBOARDING_ROLLOUT === "off" ? "control" : stored ?? fallbackVariant();

  const user = allstarUser?.user;
  const trackStep = useCallback(
    (
      action: TStepAction,
      step: TOnboardingStep,
      properties: Record<string, unknown> = {},
    ) =>
      analytics.track(`Onboarding - Step ${action}`, {
        step,
        variant,
        signupMethod: getSignupMethod(user),
        ...properties,
      }),
    [variant, user],
  );

  return {
    variant,
    isNewOnboarding: variant === "new",
    ready: stored !== null || ONBOARDING_ROLLOUT === "off",
    trackStep,
  };
};
